<template>
  <div v-loading="isLoading">
    <el-tree class="bookmark tree-w-stroke tree-editor" ref="bookmarkTree" :data="treeData" :props="defaultProps" node-key="treeKey" :expand-on-click-node="false" highlight-current draggable :allow-drag="allowDrag" :allow-drop="allowDrop" default-expand-all>
      <span class="el-tree-node__label" slot-scope="{ node, data }">
        <span v-if="node.data.subType === 'ROOT'">
          <span class="tree-node-edit-icon icon-margin-r" @click="appendBookmarkGroup(node, data)">
            <i class="fas fa-plus-circle"></i>
          </span>
          <span>{{data.label}}</span>
        </span>
        <span v-else-if="node.data.subType === 'GROUP'">
          <span v-if="node.level < 10" class="tree-node-edit-icon mini" style="margin-right:-1px; margin-left: -1px"  @click="appendBookmarkGroup(node, data)">
            <i class="far fa-plus-circle"></i>
          </span>
          <span class="tree-node-edit-icon mini content-margin-r" @click="removeBookmarkGroup(node, data)">
            <i class="far fa-minus-circle"></i>
          </span>
          <w-text-editor width="200px;" :editing="editing" :target="node" property="label" ref="editor" @endEdit="onEndEdit" @click.native="editNode(data, node)">
            <span class="tree-node-edit-label" slot="data">
              {{ node.label }}
            </span>
          </w-text-editor>
        </span>
        <span v-else>
          <span class="tree-node-edit-icon mini content-margin-r" @click="removeBookmarkNode(node, data)">
            <i class="far fa-minus-circle"></i>
          </span>
          <w-text-editor width="200px;" :editing="editing" :target="node" property="label" ref="editor" @endEdit="onEndEdit" @click.native="editNode(data, node)">
            <span class="tree-node-edit-label" slot="data">
              {{ node.label }}
            </span>
          </w-text-editor>
        </span>
      </span>
    </el-tree>
  </div>
</template>

<script>
import BookmarkAPI from '@/api/frame/bookmark';
import TreeFilter from '@/mixins/tree/filter';
import Editing from '@/mixins/editing';
import msgUtil from '@/utils/message';
import { defaultProps } from '@/constants/tree';
import DragTreeMixin from '@/views/chart/components/tree/mixins/drag-tree';
import WTextEditor from '@/views/settings/basic/LogicalGroupSettings/components/TextEditor';
import { maxBy, isEmpty, isNil, forEach, has } from 'lodash';

export default {
  name: 'WEditBookmark',
  components: {
    WTextEditor,
  },
  mixins: [Editing, TreeFilter, DragTreeMixin],
  data() {
    return {
      isLoading: false,
      activeTab: 'editBookmark',
      treeData: null,
      defaultProps,
      tempIndex: 0,
      deleteBookmarks: [],
      deleteGroups: [],
    };
  },
  computed: {
    /**
     * 로그인한 계정 정보
     * @author yelim
     * @return {[type]} [description]
     */
    userInfo() {
      return this.$store.getters.userInfo;
    },
  },
  methods: {
    init() {
      this.treeData = null;
      this.deleteBookmarks = [];
      this.deleteGroups = [];
    },
    getEditInfo() {
      const modifiedData = {
        INSERT: [],
        UPDATE: [],
        orderbyIndex: 0,
      };
      const bookmarkTree = this.$refs.bookmarkTree;
      const root = bookmarkTree.getNode(bookmarkTree.root.data[0].treeKey);
      const childNodes = root.childNodes;
      if (!isEmpty(childNodes)) {
        const parent = {
          data: {
            groupId: null,
          },
          childNodes,
        };
        this.setModifiedNodes(parent, modifiedData);
      }
      const params = {};
      if (!isEmpty(modifiedData.INSERT)) {
        params.INSERT = modifiedData.INSERT;
      }
      if (!isEmpty(modifiedData.UPDATE)) {
        params.UPDATE = modifiedData.UPDATE;
      }
      if (!isEmpty(this.deleteGroups)) {
        params.DELETEGROUP = this.deleteGroups;
      }
      if (!isEmpty(this.deleteBookmarks)) {
        params.DELETEBOOKMARK = this.deleteBookmarks;
      }
      return params;
    },
    loadData() {
      this.isLoading = true;
      BookmarkAPI.getBookmarkTree({ searchGroupFlag: false }).then((response) => {
        if (response.data.success) {
          const result = response.data.data;
          if (!isEmpty(result) && !isEmpty(result[0].children)) {
            result[0].children.sort((a, b) => a.orderbyIndex - b.orderbyIndex);
          }
          this.treeData = result;
          this.isLoading = false;
        } else {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '즐겨찾기 목록', false));
          this.isLoading = false;
        }
      }).catch((e) => {
        this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '즐겨찾기 목록', false));
        this.isLoading = false;
        throw new Error(e);
      });
    },
    allowDrop(draggingNode, dropNode, type) {
      // drop node가 root node일 경우 하위 추가만 가능하도록 한다.
      if (type === 'inner' && dropNode.data.subType === 'BOOKMARK') {
        return false;
      }
      return type === 'inner' || dropNode.level !== 1;
    },
    allowDrag(node) {
      // root node이거나 편집 중 일때는 drag를 못하게 한다.
      return this.editing.isEditing !== true && node.level !== 1;
    },
    /**
     * 트리 노드 라벨을 편집한다.
     * @param  {Object} data    클릭한 노드의 데이터
     * @param  {Object} node 클릭한 노드 객체
     */
    editNode(data, node) {
      this.startEdit(node, 'label', node.data);
    },
    /**
     * 트리 노드 라벨 편집 완료 이벤트 핸들러
     * @author Ju
     * @param  {String} value    변경된 값
     * @param  {String} oldValue 이전 값
     */
    onEndEdit(value, oldValue) {
      // 공백이 입력될 경우 이전 값으로 설정하여 빈 값이 설정되지 않도록한다.
      if (isEmpty(value)) {
        this.endEdit(oldValue);
      } else {
        this.endEdit(value);
      }
      this.editValue = null;
    },
    appendBookmarkGroup(node, data) {
      if (node.expnaded !== true) {
        node.expanded = true;
      }
      if (isNil(data.children)) {
        this.$set(data, 'children', []);
      }
      const lastChild = maxBy(data.children, ((child) => {
        const idxByType = (child.subType === 'BOOKMARK') ? child.bookmarkInfo.orderbyIndex : child.groupInfo.orderbyIndex;
        return idxByType;
      }));
      let orderbyIndex = null;
      if (isEmpty(lastChild)) {
        orderbyIndex = 0;
      } else {
        const idxByType = (lastChild.subType === 'BOOKMARK') ? lastChild.bookmarkInfo.orderbyIndex : lastChild.groupInfo.orderbyIndex;
        orderbyIndex = idxByType.orderbyIndex + 1;
      }

      const logicalGroup = {
        treeKey: `${data.treeKey}.temp_${this.tempIndex}`, // DB 반영 전이므로 임시 키를 적용한다.
        label: '새 즐겨찾기그룹',
        children: [],
        phantom: true,
        groupInfo: {
          orderbyIndex,
        },
        subType: 'GROUP',
        tmpGroupId: `tmp_${this.tempIndex}`,
      };
      this.tempIndex += 1;
      data.children.push(logicalGroup);

      this.$nextTick(() => {
        const newNode = this.$refs.bookmarkTree.getNode(logicalGroup.treeKey);
        this.editNode(newNode.data, newNode);
      });
    },
    /**
     * 노드를 삭제한다.
     * @param  {object} node [노드 객체]
     * @param  {object} data [노드 데이터 객체]
     */
    removeBookmarkGroup(node, data) {
      if (data.phantom !== true) {
        this.deleteGroups.push({
          groupId: data.groupInfo.groupId,
        });
      }
      this.addDeletedChildNodeIds(node);
      this.$refs.bookmarkTree.remove(data.treeKey);
    },
    /**
     * subtype이 bookmark인 경우에 대한 분기처리
     * @author yelim
     * @param  {[type]} node [description]
     * @param  {[type]} data [description]
     * @return {[type]}      [description]
     */
    removeBookmarkNode(node, data) {
      if (data.phantom !== true) {
        this.deleteBookmarks.push({
          bookmarkId: data.bookmarkInfo.bookmarkId,
        });
      }
      this.$refs.bookmarkTree.remove(data.treeKey);
    },
    addDeletedChildNodeIds(node) {
      if (!isEmpty(node.childNodes)) {
        forEach(node.childNodes, (childNode) => {
          if (childNode.data.phantom !== true && childNode.data.subType === 'GROUP') {
            this.deleteGroups.push({
              groupId: childNode.data.groupId,
            });
          }
          this.addDeletedChildNodeIds(childNode);
        });
      }
    },
    setModifiedNodes(parent, modifiedData) {
      const parentData = parent.data;
      const isPhantomParent = parentData.phantom ? parentData.phantom : false;
      const parentGroupId = isPhantomParent ? parentData.tmpGroupId : parentData.groupId;
      const childNodes = parent.childNodes;

      forEach(childNodes, (child) => {
        modifiedData.orderbyIndex += 1;
        const data = child.data;
        if (data.phantom === true) {
          const addedData = {
            // parentManagegroupId,
            tmpGroupId: data.tmpGroupId,
            groupName: data.label,
            orderbyIndex: modifiedData.orderbyIndex,
          };
          if (isPhantomParent) {
            addedData.tmpParentGroupId = parentGroupId;
          } else {
            addedData.parentGroupId = parentGroupId;
          }

          modifiedData.INSERT.push(addedData);
        } else {
          const updatedData = {};
          const typeId = (data.subType === 'BOOKMARK') ? data.bookmarkInfo.bookmarkId : data.groupInfo.groupId;
          updatedData.subType = data.subType;
          if ((!isNil(parentGroupId) || !isNil(typeId)) && data.treeKey !== parentData.treeKey) {
            if (isPhantomParent) {
              updatedData.tmpParentGroupId = parentGroupId;
            } else {
              updatedData.parentGroupId = parentGroupId;
            }
          }

          if (!isEmpty(data.modified) && has(data, 'modified.label')) {
            if (data.subType === 'BOOKMARK') {
              updatedData.bookmarkName = data.label;
            } else {
              updatedData.groupName = data.label;
            }
          }
          const dataInfoType = (data.subType === 'BOOKMARK') ? data.bookmarkInfo : data.groupInfo;
          if (dataInfoType.orderbyIndex !== modifiedData.orderbyIndex) {
            updatedData.orderbyIndex = modifiedData.orderbyIndex;
          }

          if (!isEmpty(updatedData)) {
            if (data.subType === 'BOOKMARK') {
              updatedData.menuId = data.bookmarkInfo.menuId;
              updatedData.managetypeId = data.bookmarkInfo.managetypeId;
              updatedData.bookmarkId = typeId;
            } else {
              updatedData.groupId = typeId;
            }
            if (!has(updatedData, 'orderbyIndex')) {
              updatedData.orderbyIndex = dataInfoType.orderbyIndex;
            }
            if (!has(updatedData, 'groupName') && !has(updatedData, 'bookmarkName')) {
              if (data.subType === 'GROUP') {
                updatedData.groupName = dataInfoType.groupName;
              } else {
                updatedData.bookmarkName = dataInfoType.bookmarkName;
              }
            }
            modifiedData.UPDATE.push(updatedData);
          }
        }

        this.setModifiedNodes(child, modifiedData);
      });
    },
  },
};
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/views/frame/components/EditBookmark.vue